import { Component, computed, inject, signal, ViewChild } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TAGInvTransaction } from '../../../../models/taginventory';
import { MessageService } from 'primeng/api';
import { Socket } from 'ngx-socket-io';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputIconModule } from 'primeng/inputicon';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { getInventoryInstanceByCode } from '../inventoryHelper';
import { InvTransactionListItemComponent } from '../../../components/inv-transaction-list-item/inv-transaction-list-item';
import { EmptyStateComponent } from '../../../components/empty-state/empty-state.component';
import { dialogConfig } from '../../../dialogs';
import {
  TransactionEditDialog,
  TransactionEditDialogData,
} from '../transaction-edit-dialog/transactionEditDialog';
import {
  BarcodeScannerSearchField,
  CodeScannedEvent,
} from '../../../components/barcode-scanner-search-field/barcode-scanner-search-field';

export interface TransactionListDialogData {
  socket: Socket;
}

@Component({
  selector: 'app-internal-transaction-list-dialog',
  imports: [
    SkeletonModule,
    DialogModule,
    ButtonModule,
    TranslatePipe,
    InputGroupModule,
    InputGroupAddonModule,
    InputIconModule,
    IconFieldModule,
    InputTextModule,
    FormsModule,
    InvTransactionListItemComponent,
    EmptyStateComponent,
    BarcodeScannerSearchField,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './transactionListDialog.html',
  styleUrl: './transactionListDialog.scss',
})
export class TransactionListDialog {
  private dialogService = inject(DialogService);
  private messageService = inject(MessageService);
  private translate = inject(TranslateService);
  private api = inject(ApiService);

  @ViewChild(BarcodeScannerSearchField) search: BarcodeScannerSearchField;

  transactions = signal<TAGInvTransaction[]>([]);
  loadingTransactions = signal(true);

  searchFilter = signal('');
  filterItemID = signal('');
  filterInstID = signal('');

  filteredTransactions = computed(() => {
    var filtered: TAGInvTransaction[] = [];

    if (this.filterItemID() == '' && this.filterInstID() == '' && this.searchFilter() == '') {
      filtered = this.transactions();
    } else
      for (let trans of this.transactions()) {
        if (this.filterItemID() != '') {
          if (this.filterInstID() != '') {
            if (trans.containsItemInstance(this.filterItemID(), this.filterInstID())) {
              if (filtered.findIndex((t) => t._id == trans._id) == -1) filtered.push(trans);
            }
          } else {
            if (trans.containsItem(this.filterItemID())) {
              if (filtered.findIndex((t) => t._id == trans._id) == -1) filtered.push(trans);
            }
          }
        } else {
          if (
            trans._id.indexOf(this.searchFilter().toLowerCase()) != -1 ||
            trans.location.toLowerCase().indexOf(this.searchFilter().toLowerCase()) != -1 ||
            trans.user.toLowerCase().indexOf(this.searchFilter().toLowerCase()) != -1
          ) {
            if (filtered.findIndex((t) => t._id == trans._id) == -1) filtered.push(trans);
          }
        }
      }

    return filtered;
  });

  constructor(
    public dialogRef: DynamicDialogRef<TransactionListDialog>,
    public config: DynamicDialogConfig<TransactionListDialogData>,
  ) {
    config.header = this.translate.translate('internal.inventory.transactionList.title')();
  }

  ngOnInit() {
    this.api.waitForAPI().then(() => {
      this.loadTransactions();
    });
  }

  loadTransactions() {
    this.loadingTransactions.set(true);

    this.api
      .getInvTransactions()
      .then((invTransactions: TAGInvTransaction[]) => {
        invTransactions.sort((a, b) => {
          return a.startdate > b.startdate ? -1 : 1;
        });

        this.transactions.set(invTransactions);
        this.loadingTransactions.set(false);

        this.initSocketHandler();
      })
      .catch((reason) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translate.translate('errors.couldNotLoadInventoryTransactions')(),
          detail: reason.message,
        });
      });
  }

  ngAfterViewInit() {
    this.resetScannerField();
  }

  ngOnDestroy(): void {
    this.config.data.socket.removeListener(
      'updateTransaction',
      this.socket_onUpdateTransaction.bind(this),
    );
    this.config.data.socket.removeListener(
      'createTransaction',
      this.socket_onCreateTransaction.bind(this),
    );
  }

  initSocketHandler() {
    this.config.data.socket.on('updateTransaction', this.socket_onUpdateTransaction.bind(this));
    this.config.data.socket.on('createTransaction', this.socket_onCreateTransaction.bind(this));
  }

  socket_onUpdateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    this.transactions.update((t) => {
      t.splice(
        t.findIndex((t) => t._id == newTrans._id),
        1,
        newTrans,
      );
      return [...t];
    });
  }

  socket_onCreateTransaction(data: any) {
    let newTrans = new TAGInvTransaction(data.transaction);

    this.transactions.update((t) => {
      t.unshift(newTrans);
      t.sort((a, b) => {
        return a.startdate > b.startdate ? -1 : 1;
      });
      return [...t];
    });
  }

  resetScannerField() {
    this.search?.reset();
    this.filterItemID.set('');
    this.filterInstID.set('');
  }

  codeScanned(code: CodeScannedEvent) {
    getInventoryInstanceByCode(code.code, this.api)
      .then((result) => {
        let item = result.item;
        let instanceID = result.instanceID;

        console.log(result);

        if (item && instanceID) {
          this.searchFilter.set('');
          this.filterItemID.set(item._id);
          this.filterInstID.set(instanceID);
        } else {
          this.searchFilter.set('');
          this.filterItemID.set(item._id);
          this.filterInstID.set('');
        }

        console.log(
          'ItemID: ' +
            this.filterItemID() +
            ' InstID: ' +
            this.filterInstID() +
            ' Text: ' +
            this.searchFilter(),
        );
      })
      .catch((reason) => {
        this.searchFilter.set(code.code);
        this.filterItemID.set('');
        this.filterInstID.set('');

        console.log(
          'ItemID: ' +
            this.filterItemID() +
            ' InstID: ' +
            this.filterInstID() +
            ' Text: ' +
            this.searchFilter(),
        );
      });
  }

  openTransaction(transaction: TAGInvTransaction) {
    const transactionEditRef = this.dialogService.open(
      TransactionEditDialog,
      dialogConfig<TransactionEditDialogData>({
        closable: true,
        data: {
          transactionId: transaction._id,
          socket: this.config.data.socket,
        },
      }),
    );

    transactionEditRef.onClose.subscribe((result) => {
      this.resetScannerField();
    });
  }

  closeDialog() {
    this.dialogRef.close(false);
  }
}
